import React from "react";
import { Link } from "react-router-dom";

import { Button, TableRowActionMenu } from "../../ui";
import LeadSenderAvatar from "../../ui/LeadSenderAvatar";

export type LinkedinAccountCardRow = {
  key: string;
  name: string;
  picture: string;
  status: "Connected" | "Disconnected" | "LinkedIn login required";
  executionMode: "CLOUD" | "EXTENSION";
  automationStatus?: string;
  automationPaused?: boolean;
  activeCampaignDisplayName: string;
  primaryCampaignId: string;
  hasActiveRunningCampaign: boolean;
  limits?: {
    invitationsUsed: number;
    invitationsQuota: number;
    messagesUsed: number;
    messagesQuota: number;
    visitsUsed: number;
    visitsQuota: number;
  };
  raw: Record<string, any>;
};

type HealthTone = "ok" | "warn" | "muted";

function deriveHealth(row: LinkedinAccountCardRow): { label: string; tone: HealthTone } {
  const activeCampaignName = String(
    row.raw?.primaryCampaignName || row.raw?.activeCampaignDisplayName || row.activeCampaignDisplayName || ""
  ).trim();
  const campaignState = String(row.raw?.primaryCampaignStatus || "").trim().toUpperCase();
  const normalizedCampaignState = String(row.raw?.campaignStatus || "").trim().toUpperCase();
  const hasCampaignMetadata = Boolean(
    row.hasActiveRunningCampaign ||
      row.raw?.hasActiveOrPausedCampaign ||
      row.primaryCampaignId ||
      activeCampaignName
  );
  const hasActiveCampaign =
    row.hasActiveRunningCampaign ||
    campaignState === "ACTIVE" ||
    normalizedCampaignState === "ACTIVE" ||
    Boolean(
      row.primaryCampaignId &&
        (row.raw?.primaryCampaignStatus === "ACTIVE" ||
          row.raw?.campaignStatus === "ACTIVE" ||
          row.raw?.hasActiveRunningCampaign)
    );
  const hasPausedCampaign =
    row.automationPaused ||
    campaignState === "PAUSED" ||
    normalizedCampaignState === "PAUSED" ||
    Boolean(
      row.primaryCampaignId &&
        (row.raw?.primaryCampaignStatus === "PAUSED" || row.raw?.campaignStatus === "PAUSED")
    );

  if (row.status === "LinkedIn login required") {
    return { label: "Needs attention", tone: "warn" };
  }
  if (row.executionMode === "EXTENSION") {
    if (
      row.automationStatus === "linkedin_login_required" ||
      row.automationStatus === "offline"
    ) {
      return { label: "Needs attention", tone: "warn" };
    }
  }

  if (row.status === "Disconnected") {
    if (hasPausedCampaign) {
      return { label: "Paused", tone: "muted" };
    }
    return { label: "Disconnected", tone: "muted" };
  }

  if (row.automationPaused || hasPausedCampaign) {
    return { label: "Paused", tone: "muted" };
  }
  if (hasActiveCampaign) {
    return { label: "Healthy", tone: "ok" };
  }
  if (hasCampaignMetadata && !hasActiveCampaign) {
    return { label: "Paused", tone: "muted" };
  }
  if (row.status === "Connected") {
    return { label: "No active campaign", tone: "muted" };
  }

  return { label: "Healthy", tone: "ok" };
}

function connectionStatusLabel(status: LinkedinAccountCardRow["status"]) {
  if (status === "Connected") return "Connected";
  if (status === "LinkedIn login required") return "LinkedIn login required";
  return "Disconnected";
}

function connectionStatusClass(status: LinkedinAccountCardRow["status"]) {
  if (status === "Connected") return "text-success";
  if (status === "LinkedIn login required") return "text-proxy-warning-text";
  return "text-muted";
}

function healthStatusClass(tone: HealthTone) {
  if (tone === "ok") return "text-success";
  if (tone === "warn") return "text-proxy-warning-text";
  return "text-[#A56810]";
}

type LinkedinAccountCardProps = {
  row: LinkedinAccountCardRow;
  isSenderLimitReached: boolean;
  onOpenSettings: (row: LinkedinAccountCardRow) => void;
  onSwitchExecutionMode?: (row: LinkedinAccountCardRow) => void;
  onDisconnectOrReconnect: (row: LinkedinAccountCardRow) => void;
};

export default function LinkedinAccountCard({
  row,
  isSenderLimitReached,
  onOpenSettings,
  onSwitchExecutionMode,
  onDisconnectOrReconnect,
}: LinkedinAccountCardProps) {
  const health = deriveHealth(row);
  const isCloud = row.executionMode === "CLOUD";
  const modeLabel = isCloud ? "Cloud" : "Chrome";
  const switchLabel = isCloud ? "Use Chrome instead" : "Use server instead";
  const workerLabel = isCloud
    ? "Server-side"
    : row.automationStatus === "online"
      ? "Online"
      : row.automationStatus === "paused"
        ? "Paused"
        : row.automationStatus === "linkedin_login_required"
          ? "LinkedIn login required"
          : "Offline";
  const hasActualActiveCampaign =
    row.hasActiveRunningCampaign ||
    String(row.raw?.primaryCampaignStatus || "").trim().toUpperCase() === "ACTIVE" ||
    String(row.raw?.campaignStatus || "").trim().toUpperCase() === "ACTIVE";

  const activityHref = hasActualActiveCampaign && row.primaryCampaignId
    ? `/campaign-management/view/${row.primaryCampaignId}`
    : "/campaign-management";

  const campaignNameFromSource = String(
    row.raw?.primaryCampaignName ||
      row.raw?.activeCampaignDisplayName ||
      row.activeCampaignDisplayName ||
      ""
  ).trim();

  const hasPausedCampaign =
    row.automationPaused ||
    String(row.raw?.primaryCampaignStatus || "").trim().toUpperCase() === "PAUSED" ||
    String(row.raw?.campaignStatus || "").trim().toUpperCase() === "PAUSED";

  const visibleCampaignName =
    row.status === "Disconnected" && !hasPausedCampaign
      ? "No active campaign"
      : row.hasActiveRunningCampaign ||
          String(row.raw?.primaryCampaignStatus || "").trim().toUpperCase() === "ACTIVE" ||
          String(row.raw?.campaignStatus || "").trim().toUpperCase() === "ACTIVE"
        ? campaignNameFromSource || "No active campaign"
        : "No active campaign";

  return (
    <article className="flex flex-col rounded-[10px] border border-line bg-white p-[17px]">
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <LeadSenderAvatar
            picture={row.picture}
            className="h-[42px] w-[42px]"
            disconnected={row.status === "Disconnected"}
          />
          <div className="min-w-0">
            <h2 className="truncate text-[15px] font-semibold leading-snug text-dark-blue">
              {row.name}
            </h2>
            <p className="mt-1 text-[11px] text-muted">LinkedIn sending account</p>
          </div>
        </div>
        <TableRowActionMenu
          onDelete={
            row.status === "Connected" || row.status === "Disconnected"
              ? () => onDisconnectOrReconnect(row)
              : undefined
          }
          deleteLabel={row.status === "Disconnected" ? "Reconnect" : "Disconnect"}
          deleteDisabled={row.status === "Disconnected" && isSenderLimitReached}
          className="shrink-0"
        />
      </header>

      <div className="mt-5 flex flex-wrap items-center gap-3 text-[11px] font-semibold">
        <span className={connectionStatusClass(row.status)}>
          ● {connectionStatusLabel(row.status)}
        </span>
        <span className={healthStatusClass(health.tone)}>● {health.label}</span>
      </div>

      <div className="mt-4 border-t border-[#eeeef2] pt-3">
        <small className="block text-[9px] font-semibold uppercase tracking-[0.08em] text-muted">
          Execution mode
        </small>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-[4px] bg-bg-light-blue px-2 py-1 text-[11px] font-semibold text-brand-blue">
            {modeLabel}
          </span>
          <span className="text-[11px] font-medium text-muted">{workerLabel}</span>
        </div>
        {row.status === "Connected" && onSwitchExecutionMode ? (
          <button
            type="button"
            onClick={() => onSwitchExecutionMode(row)}
            className="mt-2 w-fit text-left text-[11px] font-medium text-brand-blue underline underline-offset-2 hover:opacity-80"
          >
            {switchLabel}
          </button>
        ) : null}
      </div>

      <div className="mt-4 border-t border-[#eeeef2] pt-3">
        <small className="block text-[9px] font-semibold uppercase tracking-[0.08em] text-muted">
          Active campaign
        </small>
        <b className="mt-1 block text-[11px] font-semibold text-dark-blue">
          {visibleCampaignName}
        </b>
      </div>

      <footer className="mt-5 flex flex-wrap gap-2">
        <Link
          to={activityHref}
          className="inline-flex h-auto items-center rounded-[8px] border border-line bg-white px-[9px] py-[7px] text-[11px] font-semibold text-dark-black no-underline transition-colors hover:border-line hover:bg-[#f5f6f7]"
        >
          View activity
        </Link>
        <Button
          variant="outline"
          size="sm"
          className="h-auto border-line px-[9px] py-[7px] text-[11px] font-semibold hover:border-line hover:bg-[#f5f6f7]"
          onClick={() => onOpenSettings(row)}
        >
          Settings
        </Button>
      </footer>
    </article>
  );
}

export function accountRowNeedsAttention(row: LinkedinAccountCardRow): boolean {
  const health = deriveHealth(row);
  return health.tone === "warn";
}
